/**
 * The shape of a call-to-action pill in the header.
 *
 * Stated once because the bar now carries two of them — the quotation one and
 * the consultation one (§32) — and two buttons that are meant to read as a
 * pair must not drift apart one `fontSize` at a time. Horizontal padding is
 * deliberately left out: the pills differ there and nowhere else, because the
 * filled one is shown at every width and the outlined one only from `lg`, so
 * they are not working against the same space.
 *
 * `boxShadow: 'none'` is the one value here that is a decision rather than a
 * measurement. The bar is already a frosted surface floating over the page; a
 * raised button on top of it gives the eye two competing depths in 64px of
 * chrome.
 */
export const headerCtaSx = {
  whiteSpace: 'nowrap' as const,
  borderRadius: 999,
  minHeight: 'auto',
  fontSize: '0.875rem',
  textTransform: 'none' as const,
  fontWeight: 700,
  boxShadow: 'none',
};

/**
 * What the outlined pill needs while the header floats over the dark hero.
 *
 * `appVariant="primaryOutline"` is the brand red, which carries 5.08:1 on the
 * site's light surfaces and 5.85:1 on its dark ones — but the home hero
 * hard-codes `#0A0A0C` in *both* schemes, and the red measures 3.90:1 there.
 * That is under AA for a 14px label, so the floating state supplies its own
 * white border and ink, the way `navItemSx` does for the nav pills.
 *
 * The hover fill is the same 12% white those pills use, so the two respond to
 * the pointer identically.
 */
export const floatingCtaSx = {
  color: '#FFFFFF',
  borderColor: 'rgba(255,255,255,0.55)',
  '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.12)' },
};

/**
 * Horizontal padding of the two header pills, in theme spacing units (4px).
 *
 * Stated here rather than inline in `HeaderActions` because three places now
 * have to agree on them: the two real pills, and the hidden row
 * `useHeaderFit` measures the secondary one from. A twin that is measured at a
 * padding the real pill does not use is a twin that lies, and it would lie
 * quietly — the row would simply fold one item too early or too late.
 *
 * They differ from each other for the reason the docblock above gives: the
 * filled pill is on the row at every width and the outlined one only from
 * `lg`, so they are not competing for the same space. Both were trimmed by
 * 4-8px per side when the row was found to be overflowing at 1,200px; between
 * them that is 24px of the 62px that had to be found, and it is the 24px that
 * costs the least, because a pill's presence is what a visitor reads and its
 * padding is not.
 */
export const secondaryCtaPx = { lg: 5, xl: 7 } as const;

export const primaryCtaPx = { xs: 5, lg: 6, xl: 8 } as const;
